import { useEffect, useState } from 'react';
import { api } from '../api';
import {
  APPOINTMENT_EVENT_LABELS,
  APPOINTMENT_OUTCOME_COLORS,
  APPOINTMENT_OUTCOME_LABELS,
  type AppointmentJourneyResponse,
  type AppointmentOutcome,
} from '../types';
import { formatDateTime, formatDuration } from '../format';
import { Dialog } from './Dialog';

const EVENT_COLORS: Record<string, string> = {
  created: 'var(--outcome-voicemail)',
  rescheduled: 'var(--outcome-no_answer)',
  status_changed: 'var(--outcome-answered)',
  assigned_changed: 'var(--ink-muted)',
  calendar_changed: 'var(--ink-muted)',
  deleted: 'var(--outcome-failed)',
  activity: 'var(--accent)',
};

/** Reschedules carry epoch timestamps as their values; everything else is a label. */
function describe(event: AppointmentJourneyResponse['timeline'][number]): string {
  if (event.kind === 'activity') return event.body ?? 'Activity recorded in HighLevel';

  if (event.type === 'rescheduled') {
    const from = event.from ? formatDateTime(Number(event.from)) : '—';
    const to = event.to ? formatDateTime(Number(event.to)) : '—';
    return `${from} → ${to}`;
  }

  const label = (value: string | null) =>
    value ? (APPOINTMENT_OUTCOME_LABELS[value as AppointmentOutcome] ?? value) : '—';

  switch (event.type) {
    case 'created':
      // "Booked as Booked" reads as a bug; only the unusual initial states are worth saying.
      return event.to && event.to !== 'booked' ? `Booked as ${label(event.to)}` : 'Appointment booked';
    case 'status_changed':
      return `${label(event.from)} → ${label(event.to)}`;
    case 'deleted':
      return 'Deleted in HighLevel';
    default:
      return `${event.from ?? '—'} → ${event.to ?? '—'}`;
  }
}

export function AppointmentJourneyDrawer({
  appointmentId,
  onClose,
}: {
  appointmentId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<AppointmentJourneyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .appointmentJourney(appointmentId)
      .then(setData)
      .catch((cause) => setError(String(cause)));
  }, [appointmentId]);

  return (
    <Dialog title={data?.appointment.title ?? 'Appointment journey'} onClose={onClose}>
      {error ? <div className="banner error">{error}</div> : null}
      {!data && !error ? <p className="empty">Loading journey…</p> : null}

      {data ? (
        <div className="stack">
          <div className="dialog-grid">
            <div>
              <div className="cell-sub">Contact</div>
              {data.appointment.contactUrl ? (
                <a href={data.appointment.contactUrl} target="_blank" rel="noreferrer">
                  {data.appointment.contactName ?? 'Open contact'}
                </a>
              ) : (
                <span>{data.appointment.contactName ?? '—'}</span>
              )}
            </div>
            <div>
              <div className="cell-sub">Calendar</div>
              <span>{data.appointment.calendarName ?? '—'}</span>
            </div>
            <div>
              <div className="cell-sub">Now scheduled for</div>
              <span>{formatDateTime(data.appointment.startTime)}</span>
            </div>
            <div>
              <div className="cell-sub">Status</div>
              <span className="tag">
                <span
                  className="dot"
                  style={{ background: APPOINTMENT_OUTCOME_COLORS[data.appointment.outcome] }}
                  aria-hidden="true"
                />
                {APPOINTMENT_OUTCOME_LABELS[data.appointment.outcome]}
              </span>
            </div>
          </div>

          {data.reschedules > 0 ? (
            <div className="banner">
              <span>
                Rescheduled <strong>{data.reschedules}</strong>{' '}
                {data.reschedules === 1 ? 'time' : 'times'} since it was booked.
              </span>
            </div>
          ) : null}

          <div className="stack" style={{ gap: 0 }}>
            {data.timeline.map((event) => (
              <div className="journey-row" key={event.id}>
                <span
                  className="journey-dot"
                  style={{ background: EVENT_COLORS[event.type] ?? 'var(--accent)' }}
                />
                <div className="journey-body">
                  <div className="spread">
                    <span className="cell-strong">
                      {APPOINTMENT_EVENT_LABELS[event.type] ?? event.type}
                    </span>
                    <span className="note-meta">{formatDateTime(event.occurredAt)}</span>
                  </div>
                  <div className="note-body">{describe(event)}</div>
                  <div className="cell-sub">
                    {event.msUntilNext !== null
                      ? `${formatDuration(Math.round(event.msUntilNext / 1000))} until the next change · `
                      : ''}
                    {event.detectedBy === 'webhook'
                      ? 'from a live webhook'
                      : event.detectedBy === 'activity'
                        ? 'from HighLevel’s activity log'
                        : 'observed by the sync'}
                    {event.actorName
                      ? ` · by ${event.actorName}${
                          event.actorSource === 'activity' ? ' (matched from the activity log)' : ''
                        }`
                      : ''}
                  </div>
                </div>
              </div>
            ))}
            {data.timeline.length === 0 ? (
              <p className="empty">
                No changes recorded yet. The journey builds from the point this dashboard started
                watching, so an appointment that has not moved since then has nothing to show.
              </p>
            ) : null}
          </div>

          <p className="small muted">
            Booking names the person who booked it, from the calendar event itself. Reschedules and
            cancellations carry no actor in HighLevel’s API, so those are attributed from the
            contact’s activity log where a matching entry exists —{' '}
            {data.attributed} of {data.timeline.length} entries name a person.            {data.showsAllActivity ? null : (
              <>
                {' '}
                This contact has other appointments, and HighLevel writes activity against the
                contact rather than the record — so entries that could not be matched to a change
                here are withheld rather than shown against the wrong one.
              </>
            )}
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
